import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { mockOrbitCampaigns } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import { FiX, FiCheckCircle, FiClock, FiPlayCircle } from 'react-icons/fi';

export default function OrbitBoard() {
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const getOrbitRadius = (level) => {
    switch(level) {
      case 1: return 120;
      case 2: return 220;
      case 3: return 320;
      default: return 100;
    }
  };

  return (
    <div className="h-full w-full relative overflow-hidden flex items-center justify-center bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-[#111] via-[#050505] to-[#050505]">
      
      {/* Background Grid */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>

      {/* Orbit System Container */}
      <div className="relative w-[800px] h-[800px] flex items-center justify-center">
        
        {/* Core / Sun */}
        <div className="absolute z-10 w-24 h-24 rounded-full bg-gradient-to-br from-[#FF5A5F] to-[#802a2d] shadow-[0_0_40px_rgba(255,90,95,0.5)] flex items-center justify-center flex-col border border-[#FF5A5F]/50">
          <span className="text-white font-bold text-sm tracking-wider">BRAND</span>
          <span className="text-[#FF5A5F] text-[10px] font-mono">CORE</span>
        </div>

        {/* Orbits */}
        {[1, 2, 3].map((level) => {
          const radius = getOrbitRadius(level);
          const isReverse = level % 2 === 0;
          return (
            <div 
              key={`orbit-${level}`}
              className={`absolute rounded-full border border-[#2a2a2a] border-dashed opacity-50 ${isReverse ? 'animate-spin-reverse-slow' : 'animate-spin-slow'}`}
              style={{ width: radius * 2, height: radius * 2 }}
            >
              {/* Nodes on this orbit */}
              {mockOrbitCampaigns.filter(c => c.orbit === level).map(campaign => {
                const angleRad = (campaign.angle * Math.PI) / 180;
                const x = radius * Math.cos(angleRad) + radius;
                const y = radius * Math.sin(angleRad) + radius;

                return (
                  <div 
                    key={campaign.id}
                    className="absolute"
                    style={{ left: x, top: y, transform: 'translate(-50%, -50%)' }}
                  >
                    {/* Counter rotation wrapper to keep text upright */}
                    <div className={isReverse ? 'orbit-node-container-reverse' : 'orbit-node-container'}>
                      <motion.button
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => setSelectedCampaign(campaign)}
                        className="relative group cursor-pointer"
                      >
                        <div 
                          className="w-4 h-4 rounded-full shadow-lg z-20 relative"
                          style={{ backgroundColor: campaign.color, boxShadow: `0 0 15px ${campaign.color}` }}
                        />
                        <div className="absolute top-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#141414] border border-[#2a2a2a] px-3 py-1 rounded text-xs whitespace-nowrap text-white z-30 pointer-events-none">
                          {campaign.name}
                        </div>
                      </motion.button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="absolute bottom-8 left-8 glass-panel p-4">
        <h4 className="text-xs text-gray-500 uppercase tracking-widest mb-3">Orbit Map</h4>
        <div className="space-y-2">
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[#FF5A5F]"></div><span className="text-xs text-gray-300">Inner: Active (7 Days)</span></div>
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[#10B981]"></div><span className="text-xs text-gray-300">Mid: Planning (30 Days)</span></div>
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-[#3B82F6]"></div><span className="text-xs text-gray-300">Outer: Future (Quarterly)</span></div>
        </div>
      </div>

      {/* Detail Panel */}
      <AnimatePresence>
        {selectedCampaign && (
          <motion.div 
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            className="absolute top-0 right-0 w-96 h-full glass-panel border-r-0 border-t-0 border-b-0 rounded-none z-40 p-6 shadow-[-10px_0_30px_rgba(0,0,0,0.5)]"
          >
            <button onClick={() => setSelectedCampaign(null)} className="absolute top-6 right-6 text-gray-400 hover:text-white">
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
            
            <div className="mt-8">
              <div className="inline-block px-2 py-1 rounded text-[10px] uppercase tracking-wider mb-4 font-medium" style={{ backgroundColor: `${selectedCampaign.color}20`, color: selectedCampaign.color }}>
                {selectedCampaign.status}
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">{selectedCampaign.name}</h2>
              <p className="text-sm text-gray-400 mb-8">Cross-channel campaign targeting Q3 objectives. Orbit level {selectedCampaign.orbit}.</p>

              <div className="space-y-6">
                <div>
                  <div className="flex justify-between text-xs mb-2">
                    <span className="text-gray-400">Progress</span>
                    <span className="text-white">{selectedCampaign.progress}%</span>
                  </div>
                  <div className="w-full bg-[#1a1a1a] rounded-full h-1.5">
                    <div className="h-1.5 rounded-full" style={{ width: `${selectedCampaign.progress}%`, backgroundColor: selectedCampaign.color }}></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#141414] p-4 rounded-xl border border-[#2a2a2a]">
                    <SafeIcon icon={FiPlayCircle} className="text-gray-500 mb-2" />
                    <div className="text-xl font-bold text-white">12</div>
                    <div className="text-xs text-gray-500">Assets Live</div>
                  </div>
                  <div className="bg-[#141414] p-4 rounded-xl border border-[#2a2a2a]">
                    <SafeIcon icon={FiClock} className="text-gray-500 mb-2" />
                    <div className="text-xl font-bold text-white">4</div>
                    <div className="text-xs text-gray-500">Days Left</div>
                  </div>
                </div>

                <div className="pt-6 border-t border-[#2a2a2a]">
                  <h4 className="text-sm font-medium text-white mb-4">Recent Activity</h4>
                  <div className="space-y-4">
                    {[1, 2].map((i) => (
                      <div key={i} className="flex gap-3">
                        <div className="mt-1"><SafeIcon icon={FiCheckCircle} className="text-green-500" /></div>
                        <div>
                          <p className="text-sm text-gray-300">Approved draft for Twitter</p>
                          <p className="text-xs text-gray-500">2 hours ago</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <button className="w-full btn-primary mt-8" style={{ backgroundColor: selectedCampaign.color, boxShadow: `0 0 15px ${selectedCampaign.color}40` }}>
                  Open Campaign Board
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}